import { z } from 'zod';
import { ClaimPaymentStatus, CrewClaimResolutionMethodEnum, CrewClaimRollupStatusCode, InspectionGradeCode, PrimaryEntity, ReportColumnsClaim, ReportColumnsRegistration, ReportColumnsShipping, StoreProductStatus, } from '../../enums/crew.js';
import { ClaimReasonEnum, ClaimResolutionMethodEnum } from '../../enums/gsp.js';
import { TrackingStatusCode } from '../../enums/tracking.js';
import { enumToZodLiteralUnion, helperSchema } from '../../zodExtensions.js';
import { claimRiskLevel } from './claimRiskAssessment.js';
const arrayWithMinOne = (schema) => z.array(schema).min(1);
const nullableDateString = z.string().datetime().nullable();
export const dateRange = z.object({
    start: nullableDateString,
    end: nullableDateString,
});
const baseQuery = z.object({
    searchTerm: helperSchema.optionalizedString,
    createdOn: dateRange.optional(),
});
const registrationQuery = z.object({
    kind: z.literal(PrimaryEntity.Registration),
    filters: z
        .object({
        salesChannels: arrayWithMinOne(z.string()).optional(),
        estimatedPurchaseDate: dateRange.optional(),
        hasClaim: z.boolean().optional(),
        hasBeenCanceled: z.boolean().optional(),
        registrationTaggedWith: arrayWithMinOne(z.string()).optional(),
        productVariantIds: arrayWithMinOne(z.string()).optional(),
    })
        .merge(baseQuery),
});
const returnOrWarranty = z.object({
    hasError: z.boolean().optional(),
    productVariantIds: arrayWithMinOne(z.string()).optional(),
    claimReasonIds: arrayWithMinOne(z.string()).optional(),
    claimReasonDetailIds: arrayWithMinOne(z.string()).optional(),
    inspectionGrade: arrayWithMinOne(z.enum(InspectionGradeCode)).optional(),
    hasInspection: z.boolean().optional(),
    wasResolvedExternally: z.boolean().optional(),
    riskLevel: arrayWithMinOne(z.enum(claimRiskLevel)).optional(),
    isDenied: z.boolean().optional(),
    hasShipment: z.boolean().optional(),
    source: arrayWithMinOne(z.union([z.literal('Merchant_App'), z.literal('Customer_App')])).optional(),
    shipmentStatus: arrayWithMinOne(enumToZodLiteralUnion(TrackingStatusCode)).optional(),
    statusCodes: arrayWithMinOne(enumToZodLiteralUnion(CrewClaimRollupStatusCode)).optional(),
    claimTaggedWith: arrayWithMinOne(z.string()).optional(),
    requestedResolutionMethods: arrayWithMinOne(enumToZodLiteralUnion(CrewClaimResolutionMethodEnum)).optional(),
    paymentStatuses: arrayWithMinOne(enumToZodLiteralUnion(ClaimPaymentStatus)).optional(),
    inspectionCreatedOn: dateRange.optional(),
    wasOutsideEligibilityWindow: z.boolean().optional(),
});
// ----- return or warranty query -----
const returnQuery = z.object({
    kind: z.literal(PrimaryEntity.Return),
    filters: returnOrWarranty.merge(baseQuery).extend({
        hasInstantExchange: z.boolean().optional(),
        hasUpsellExchange: z.boolean().optional(),
        hasShopNowItems: z.boolean().optional(),
    }),
});
const warrantyQuery = z.object({
    kind: z.literal(PrimaryEntity.Warranty),
    filters: returnOrWarranty.merge(baseQuery).extend({
        salesChannels: arrayWithMinOne(z.string()).optional(),
        hasRegistration: z.boolean().optional(),
    }),
});
const shippingClaimListQuery = z.object({
    kind: z.literal(PrimaryEntity.Shipping),
    filters: z
        .object({
        resolutionMethods: arrayWithMinOne(enumToZodLiteralUnion(ClaimResolutionMethodEnum)).optional(),
        claimReasons: arrayWithMinOne(enumToZodLiteralUnion(ClaimReasonEnum)).optional(),
        shippingClaimTaggedWith: arrayWithMinOne(z.string()).optional(),
    })
        .merge(baseQuery),
});
const orderListQuery = z.object({
    kind: z.literal(PrimaryEntity.Order),
    filters: baseQuery,
});
const productListQuery = z.object({
    kind: z.literal('Product'),
    filters: z
        .object({
        isOffPlatform: z.boolean().optional(),
        productGroupIds: arrayWithMinOne(z.string()).optional(),
        productStatuses: arrayWithMinOne(enumToZodLiteralUnion(StoreProductStatus)).optional(),
    })
        .merge(baseQuery),
});
const baseSearchReq = z.object({
    cursor: z
        .object({
        id: z.number(),
        direction: z.union([z.literal('next'), z.literal('prev')]),
    })
        .nullable(),
    orderBy: z.union([z.literal('asc'), z.literal('desc')]),
    take: z.number().default(50),
    shouldUseOpenSearch: z.boolean().optional(),
});
const shippingClaimSearchReq = shippingClaimListQuery.extend(baseSearchReq.shape);
const orderSearchReq = orderListQuery.extend(baseSearchReq.shape);
export const searchFilterReq = z.discriminatedUnion('kind', [
    shippingClaimSearchReq,
    returnQuery.merge(baseSearchReq),
    warrantyQuery.merge(baseSearchReq),
    registrationQuery.merge(baseSearchReq),
    orderSearchReq,
    productListQuery.merge(baseSearchReq),
]);
// the concierge search reads every merchant's data — orders and shipping claims only
export const conciergeSearchFilterReq = z.discriminatedUnion('kind', [
    shippingClaimSearchReq,
    orderSearchReq,
]);
// ------------ views ------------
export const RollingDatePreset = {
    today: '0d',
    last7Days: '7d',
    last30Days: '30d',
    last90Days: '90d',
    last12Months: '12m',
};
export const viewDateValue = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('preset'),
        preset: z.enum(RollingDatePreset),
    }),
    z.object({
        kind: z.literal('custom'),
        range: dateRange,
    }),
]);
const baseViewFilters = baseQuery.extend({
    createdOn: viewDateValue.optional(),
});
const returnViewFilters = returnQuery.extend({
    filters: returnQuery.shape.filters
        .extend({
        inspectionCreatedOn: viewDateValue.optional(),
    })
        .merge(baseViewFilters),
});
const warrantyViewFilters = warrantyQuery.extend({
    filters: warrantyQuery.shape.filters
        .extend({
        inspectionCreatedOn: viewDateValue.optional(),
    })
        .merge(baseViewFilters),
});
const registrationViewFilters = registrationQuery.extend({
    filters: registrationQuery.shape.filters
        .extend({
        estimatedPurchaseDate: viewDateValue.optional(),
    })
        .merge(baseViewFilters),
});
const shippingClaimListViewFilters = shippingClaimListQuery.extend({
    filters: shippingClaimListQuery.shape.filters.merge(baseViewFilters),
});
const orderListViewFilters = orderListQuery.extend({
    filters: orderListQuery.shape.filters.merge(baseViewFilters),
});
const productListViewFilters = productListQuery.extend({
    filters: productListQuery.shape.filters.merge(baseViewFilters),
});
const baseView = z.object({
    id: z.number(),
    name: helperSchema.nonEmptyString.max(40),
});
export const viewFilters = z.discriminatedUnion('kind', [
    returnViewFilters.pick({ kind: true, filters: true }),
    warrantyViewFilters.pick({ kind: true, filters: true }),
    registrationViewFilters.pick({ kind: true, filters: true }),
    shippingClaimListViewFilters.pick({ kind: true, filters: true }),
    orderListViewFilters.pick({ kind: true, filters: true }),
    productListViewFilters.pick({ kind: true, filters: true }),
]);
export const storeUiView = z.discriminatedUnion('kind', [
    returnViewFilters.merge(baseView),
    warrantyViewFilters.merge(baseView),
    registrationViewFilters.merge(baseView),
    shippingClaimListViewFilters.merge(baseView),
    orderListViewFilters.merge(baseView),
    productListViewFilters.merge(baseView),
]);
export const storeUiViewCreate = z.discriminatedUnion('kind', [
    returnViewFilters.merge(baseView.omit({ id: true })),
    warrantyViewFilters.merge(baseView.omit({ id: true })),
    registrationViewFilters.merge(baseView.omit({ id: true })),
    shippingClaimListViewFilters.merge(baseView.omit({ id: true })),
    orderListViewFilters.merge(baseView.omit({ id: true })),
    productListViewFilters.merge(baseView.omit({ id: true })),
]);
// ------------ responses ------------
export const pageInfo = z.object({
    nextCursor: z.number().nullable(),
    prevCursor: z.number().nullable(),
});
const reportPresentationBase = z.object({
    filename: z.string().optional(),
});
const claimReportPresentation = reportPresentationBase.extend({
    columns: z.array(z.enum(ReportColumnsClaim)),
    lineItemCustomFieldIds: z.array(z.number()),
    inspectionCustomFieldIds: z.array(z.number()),
});
const registrationReportPresentation = reportPresentationBase.extend({
    columns: z.array(z.enum(ReportColumnsRegistration)),
    registrationCustomFieldIds: z.array(z.number()),
});
const shippingReportPresentation = reportPresentationBase.extend({
    columns: z.array(z.enum(ReportColumnsShipping)),
});
export const reportDefinitionByEntity = {
    Return: claimReportPresentation,
    Warranty: claimReportPresentation,
    Registration: registrationReportPresentation,
    Shipping: shippingReportPresentation,
};
export const reportFiltersByEntity = {
    Return: returnViewFilters.shape.filters,
    Warranty: warrantyViewFilters.shape.filters,
    Registration: registrationViewFilters.shape.filters,
    Shipping: shippingClaimListViewFilters.shape.filters,
};
const returnReport = z.object({
    kind: z.literal(PrimaryEntity.Return),
    filters: reportFiltersByEntity.Return,
    jsonDefinition: reportDefinitionByEntity.Return,
});
const warrantyReport = z.object({
    kind: z.literal(PrimaryEntity.Warranty),
    filters: reportFiltersByEntity.Warranty,
    jsonDefinition: reportDefinitionByEntity.Warranty,
});
const registrationReport = z.object({
    kind: z.literal(PrimaryEntity.Registration),
    filters: reportFiltersByEntity.Registration,
    jsonDefinition: reportDefinitionByEntity.Registration,
});
const shippingReport = z.object({
    kind: z.literal(PrimaryEntity.Shipping),
    filters: reportFiltersByEntity.Shipping,
    jsonDefinition: reportDefinitionByEntity.Shipping,
});
export const reportInlineRunRequest = z.discriminatedUnion('kind', [
    returnReport,
    warrantyReport,
    registrationReport,
    shippingReport,
]);
const named = {
    name: helperSchema.nonEmptyString,
    frequency: z.enum(['Daily', 'Weekly', 'Monthly']).optional(),
    exportDay: z.number().optional(),
    exportEmails: helperSchema.emailList.optional(), // comma-separated list of emails to send the export to
};
export const reportDefinitionCreateRequest = z.discriminatedUnion('kind', [
    returnReport.extend(named),
    warrantyReport.extend(named),
    registrationReport.extend(named),
    shippingReport.extend(named),
]);
export const reportDefinitionUpdateRequest = reportDefinitionCreateRequest;
const persisted = {
    id: z.number(),
    storeId: z.number(),
    createdOn: z.string(),
    updatedOn: z.string().nullable(),
};
export const reportDefinitionResponse = z.discriminatedUnion('kind', [
    returnReport.extend({ ...named, ...persisted }),
    warrantyReport.extend({ ...named, ...persisted }),
    registrationReport.extend({ ...named, ...persisted }),
    shippingReport.extend({ ...named, ...persisted }),
]);
//# sourceMappingURL=filters.js.map